// API base URL is baked in at build time from REACT_APP_API_BASE.
// Local dev defaults to the FastAPI dev server on :8000.
const BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

async function get(path, params = {}) {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
  ).toString();
  const url = `${BASE}${path}${qs ? '?' + qs : ''}`;
  const res = await fetch(url, { credentials: 'include' });
  if (res.status === 401) {
    // Bubble up so the app shell can show the login screen.
    const e = new Error('login required');
    e.status = 401;
    throw e;
  }
  if (!res.ok) {
    const text = await res.text();
    const e = new Error(`API error ${res.status}: ${url} — ${text}`);
    e.status = res.status;
    throw e;
  }
  return res.json();
}

async function post(path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok) {
    const text = await res.text();
    const e = new Error(`API error ${res.status}: ${path} — ${text}`);
    e.status = res.status;
    throw e;
  }
  return res.json();
}

export const api = {
  auth: {
    me:        ()       => get('/api/auth/me'),
    devRoles:  ()       => get('/api/auth/dev-roles'),
    devLogin:  (email)  => post('/auth/dev-login', { email }),
    logout:    ()       => fetch(`${BASE}/auth/logout`, { credentials: 'include' }),
    loginUrl:  ()       => `${BASE}/auth/login`,
  },
  kpi: {
    summary: (period, ref, billingEntity) => get('/api/kpi/summary', { period, ref, billing_entity: billingEntity }),
    margins: (month)        => get('/api/kpi/margins', { month }),
    months:  ()             => get('/api/kpi/months'),
  },
  projects: {
    list:    (params) => get('/api/projects', params),
    totals:  ()       => get('/api/projects/totals'),
    billingEntities: () => get('/api/projects/billing-entities'),
    detail:      (id, month) => get(`/api/projects/${id}`, { month }),
    assignments: (id)       => get(`/api/projects/${id}/assignments`),
    clients: (month)  => get('/api/projects/clients/summary', { month }),
    rateCard:        (id)         => get(`/api/settings/projects/${id}/rate-card`),
    pinRateCard:     (id, body)   => post(`/api/settings/projects/${id}/rate-card/pin`, body),
    unpinRateCard:   (id)         => post(`/api/settings/projects/${id}/rate-card/unpin`),
    earnedValue:     (id)         => get(`/api/projects/${id}/earned-value`),
    milestones:      (id)         => get(`/api/projects/${id}/milestones`),
    addMilestone:    (id, body)   => post(`/api/projects/${id}/milestones`, body),
    invoices:        (id)         => get(`/api/projects/${id}/invoices`),
    addInvoice:      (id, body)   => post(`/api/projects/${id}/invoices`, body),
  },
  cac: {
    summary:     (period, ref) => get('/api/cac', { period, ref }),
    monthly:     ()            => get('/api/cac/monthly'),
    saveMonthly: (body)        => post('/api/cac/monthly', body),
  },
  recon: {
    periods:     (granularity) => get('/api/recon/periods', { granularity }),
    syncStatus:  ()       => get('/api/recon/sync-status'),
    triggerSync: ()       => post('/api/recon/sync'),
    summary:     (period) => get('/api/recon/summary', { period }),
    users:       (period) => get('/api/recon/users', { period }),
    adjustments: (period) => get('/api/recon/adjustments', { period }),
    saveAdjustment: (period, body) => fetch(`${BASE}/api/recon/adjustments?period=${encodeURIComponent(period)}`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }).then(async (r) => {
      if (!r.ok) { const t = await r.text(); const e = new Error(`API error ${r.status}: ${t}`); e.status = r.status; throw e; }
      return r.json();
    }),
    projects:    (period, billingEntity) => get('/api/recon/projects', { period, billing_entity: billingEntity }),
    payroll:     (period) => get('/api/recon/payroll', { period }),
    exceptions:  (period) => get('/api/recon/exceptions', { period }),
    config:      ()       => get('/api/recon/config'),
    saveConfig:  (body)   => fetch(`${BASE}/api/recon/config`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }).then((r) => r.json()),
    rates:       ()       => get('/api/recon/rates'),
    setRate:     (employeeId, hourly_rate) => fetch(`${BASE}/api/recon/rates/${encodeURIComponent(employeeId)}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ hourly_rate }),
    }).then((r) => r.json()),
    generate:    (period) => post(`/api/recon/generate${period ? `?period=${period}` : ''}`),
    overtime:    (period, status, billingEntity) => get('/api/recon/overtime', { period, status, billing_entity: billingEntity }),
    updateOvertime: (id, body) => fetch(`${BASE}/api/recon/overtime/${id}`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    }).then((r) => r.json()),
    bulkApprove: (period) => post(`/api/recon/overtime/bulk-approve${period ? `?period=${period}` : ''}`),
    exportUrl:   (period) => `${BASE}/api/recon/overtime/export${period ? `?period=${period}` : ''}`,
  },
  revenue: {
    months:         ()      => get('/api/revenue/months'),
    summary:        (month) => get('/api/revenue/summary', { month }),
    ar:             ()      => get('/api/revenue/ar'),
    clients:        (month) => get('/api/revenue/clients', { month }),
    reconciliation: (month) => get('/api/revenue/reconciliation', { month }),
  },
  reporting: {
    periods:     ()       => get('/api/reporting/periods'),
    utilisation: (period) => get('/api/reporting/utilisation', { period }),
    trend:       (n)      => get('/api/reporting/utilisation/trend', n ? { n } : {}),
  },
  schedule: {
    filters: () => get('/api/schedule/filters'),
    grid:    (params) => get('/api/schedule/', params),
  },
  admin: {
    users:    ()                       => get('/api/admin/users'),
    audit:    (email)                  => get('/api/admin/users/audit', email ? { email } : {}),
    setRole:  (email, role, notes)     => fetch(`${BASE}/api/admin/users/${encodeURIComponent(email)}/role`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role, notes }),
    }).then(async (r) => {
      if (!r.ok) {
        const t = await r.text();
        const e = new Error(`API error ${r.status}: ${t}`);
        e.status = r.status;
        throw e;
      }
      return r.json();
    }),
  },
  sandbox: {
    projects:   ()                 => get('/api/sandbox/projects'),
    inputs:     (projectId, month) => get('/api/sandbox/inputs', { project_id: projectId, month }),
    scenarios:  ()                 => get('/api/sandbox/scenarios'),
    scenario:   (id)               => get(`/api/sandbox/scenarios/${id}`),
    saveScenario: (name, graph, notes) => post('/api/sandbox/scenarios', { name, graph, notes }),
    deleteScenario: (id) => fetch(`${BASE}/api/sandbox/scenarios/${id}`, {
      method: 'DELETE', credentials: 'include',
    }).then((r) => r.json()),
    activeFormula:  ()                  => get('/api/sandbox/active-formula'),
    formulaHistory: ()                  => get('/api/sandbox/formula-history'),
    validate:       (label, graph)      => post('/api/sandbox/validate', { label, graph }),
    promote:        (label, graph, notes) => post('/api/sandbox/promote', { label, graph, notes }),
  },
  settings: {
    getRateCard:     ()       => get('/api/settings/rate-card'),
    saveRateCard:    (data)   => post('/api/settings/rate-card', data),
    auditRateCard:   ()       => get('/api/settings/rate-card/audit'),
    rateCardOn:      (date)   => get(`/api/settings/rate-card/on/${date}`),
    getSalaries:     ()       => get('/api/settings/salaries'),
    saveSalaries:    (data)   => post('/api/settings/salaries', data),
    auditSalaries:   (emp)    => get('/api/settings/salaries/audit', emp ? { employee_id: emp } : {}),
    getOverhead:     ()       => get('/api/settings/overhead'),
    saveOverhead:    (data)   => post('/api/settings/overhead', data),
    auditOverhead:   ()       => get('/api/settings/overhead/audit'),
  },
};
